import { EndAction } from "./End";
import { AssistantNodeAction, ConditionalNodeType, NodeType } from "./NodeType";

export {
  nodeCatalog,
  TCatalogNode,
  getValidNewNodesToAdd,
  getValidNewNodesToInsert,
  getDuplicatableNodes,
  getNodeTypeInfo,
};

function getValidNewNodesToAdd(sourceNode) {
  const validNodes = nodeCatalog[sourceNode.type] ?? [];
  return validNodes;
}

function getValidNewNodesToInsert(sourceNode, targetNode) {
  return getValidNewNodesToAdd(sourceNode).filter((node) =>
    nodeCatalog[node.type]?.some((n) => n.type === targetNode.type),
  );
}

function getDuplicatableNodes(validNodes, availableNodes, excludeNodeIds = []) {
  return availableNodes.filter(
    (node) =>
      validNodes.includes(node.type) && !excludeNodeIds.includes(node.id),
  );
}

type TCatalogNode = {
  label: string;
  type: (typeof NodeType)[keyof typeof NodeType];
  subType?:
    | (typeof AssistantNodeAction)[keyof typeof AssistantNodeAction]
    | (typeof ConditionalNodeType)[keyof typeof ConditionalNodeType];
  description?: string;
};

const webhookTrigger = {
  label: "Webhook Trigger",
  type: NodeType.WEBHOOK_TRIGGER,
  description:
    "Start the pipeline through an HTTP POST to the Pipeline's Webhook URL.",
};

const voice: TCatalogNode = {
  label: "Call",
  type: NodeType.ASSISTANT,
  subType: AssistantNodeAction.VOICE_CALL,
  description: "Use a configured assistant to place a voice call",
};

const whatsappMessage: TCatalogNode = {
  label: "Whatsapp Message",
  type: NodeType.ASSISTANT,
  subType: AssistantNodeAction.WHATSAPP_MESSAGE,
  description: "Use a configured assistant to send a WhatsApp message",
};

const conditionRules = {
  label: "Branch Conditions",
  type: NodeType.CONDITIONAL,
  subType: ConditionalNodeType.CONDITIONAL_RULE,
  description: "Branch the pipeline based on field values",
};

const externalApi = {
  label: "API",
  type: NodeType.EXTERNAL_API,
  description: "Make an HTTP request to any external API",
};

const end = {
  label: "End",
  type: NodeType.END,
  subType: EndAction.NONE,
  description: "Terminate the Pipeline",
};

const endWithArchiveConversation = {
  label: "End - Archive Conversation",
  type: NodeType.END,
  subType: EndAction.ARCHIVE_CONVERSATION,
  description: "Terminate the Pipeline and archive the conversation"
}

const endWithWebhookNotification = {
  label: "End - Webhook Notification",
  type: NodeType.END,
  subType: EndAction.WEBHOOK_NOTIFICATION,
  description: "Terminate the Pipeline and Send Webhook Notification"
}

const waitForCallback = {
  label: "Wait For Callback",
  type: NodeType.WAIT_FOR_CALLBACK,
  description:
    "Pause and Resume Execution via an HTTP Request to Callback URL.",
};

const delay = {
  label: "Delay",
  type: NodeType.DELAY,
  description: "Pause Execution with fixed, scheduled, or dynamic delays."
};

const getNodeTypeInfo = (node) => {
  const { type } = node;

  switch (type) {
    case NodeType.WEBHOOK_TRIGGER:
    case NodeType.EXTERNAL_API:
    case NodeType.WAIT_FOR_CALLBACK:
    case NodeType.DELAY:
    default:
      return { type, subType: "default" };
    case NodeType.ASSISTANT:
      return { type, subType: node.data.action };
    case NodeType.CONDITIONAL:
      return { type, subType: node.data.mode };
    case NodeType.END: {
      return { type, subType: node.data.endAction ?? EndAction.NONE };
    }
  }
};

const nodeCatalog = {
  placeholder: [webhookTrigger],
  [NodeType.WEBHOOK_TRIGGER]: [voice, whatsappMessage, externalApi, delay, conditionRules, end, endWithArchiveConversation],
  [NodeType.ASSISTANT]: [voice, whatsappMessage, conditionRules, externalApi, delay, waitForCallback, end, endWithArchiveConversation],
  [NodeType.CONDITIONAL]: [voice, whatsappMessage, externalApi, delay, waitForCallback, end, endWithArchiveConversation],
  [NodeType.EXTERNAL_API]: [voice, whatsappMessage, conditionRules, delay, externalApi, waitForCallback, end, endWithArchiveConversation],
  [NodeType.WAIT_FOR_CALLBACK]: [voice, whatsappMessage, externalApi, delay, conditionRules, waitForCallback, end, endWithArchiveConversation],
  [NodeType.DELAY]: [voice, whatsappMessage, externalApi, end, endWithArchiveConversation],
} as const;
